import classNames from 'classnames'
import { observer } from 'mobx-react'
import { array, arrayOf, bool, number, oneOf, shape, string } from 'prop-types'
import { Loading } from '@winnin/winnin_ui.components.loading'

import CreatorCard from 'components/CreatorCard'
import CreatorListItem from 'components/CreatorListItem'
import { ProviderIcon } from 'components/provider-icon'
import { T, useTranslate } from 'lib/tolgee'
import { CreatorsComparisonStore, FeatureFlagsStore, UserStore } from 'stores'
import { TOLGEE_GLOBALS } from 'utils'

import SeeMoreCard from '../SeeMoreCard'

import './index.scss'

function CustomHeader({ headerNames }) {
  return (
    <>
      {headerNames.map(item => (
        <div className={item.selected ? 'custom-header-label-selected' : ''} key={item?.name}>
          {item?.name} - <T keyName="CreatorsGrid-customHeaderLabelAllTime">All Time</T>
        </div>
      ))}
    </>
  )
}

function HeaderItemFeatureFlag({ itemsHeader }) {
  const { name, featureFlag, provider } = itemsHeader

  if (FeatureFlagsStore.featureFlagsValues[featureFlag]) {
    return (
      <div>
        <div>
          <ProviderIcon
            icon={provider}
            className="provider-icon-header-grid"
            height={20}
            width={provider === 'yt' ? 26 : 20}
          />
        </div>
        {name}
      </div>
    )
  }
  return null
}

function CreatorsGrid({
  creators,
  highlight,
  mostLikedVideo,
  showCompareButton,
  topicMetris,
  hideBrandTag = false,
  hideTier = false,
  disablePin = false,
  customHeader,
  customListItem = false,
  seeMoreAsCard,
  onSeeMoreCard,
  onCreatorClickCallback,
  isPublic = false,
  shouldEnableCreatorClick = false,
  onSeeVideos,
  viewMode,
}) {
  const t = useTranslate()

  const { creatorsComparison, handleSelect, isCreatorInComparison, firstCreatorProvider } = CreatorsComparisonStore

  const CreatorItem = viewMode === 'grid' ? CreatorCard : CreatorListItem
  const creatorsGridClasses = classNames(`creators-${viewMode}`, { 'creators-grid-highlight': highlight })

  const creatorListHeaderClass = classNames('creators-list-header', {
    'with-twitch': !FeatureFlagsStore.featureFlagsValues['istiktokenabled'],
    'with-twitch-tiktok': FeatureFlagsStore.featureFlagsValues['istiktokenabled'],
    'custom-header': !!customHeader?.length,
  })

  return (
    <div className={creatorsGridClasses}>
      {viewMode === 'list' && creators.length ? (
        <div className={creatorListHeaderClass}>
          <div></div>
          <div></div>
          <div></div>

          {customHeader?.length ? (
            <CustomHeader headerNames={customHeader} />
          ) : (
            <>
              <div>
                <div>
                  <ProviderIcon height={20} width={20} icon="yt" className="icon" />
                </div>
                <T keyName="CreatorsGrid-TableHeaderSubscribers">Subscribers</T>
              </div>
              <div>
                <div>
                  <ProviderIcon height={20} width={20} icon="in" className="icon" />
                </div>
                <T keyName="CreatorsGrid-TableHeaderSubscribers">Subscribers</T>
              </div>
              <div>
                <div>
                  <ProviderIcon height={20} width={20} icon="fb" className="icon" />
                </div>
                <T keyName="CreatorsGrid-TableHeaderSubscribers">Subscribers</T>
              </div>

              <HeaderItemFeatureFlag
                itemsHeader={{
                  name: t({
                    defaultValue: 'Subscribers',
                    key: 'CreatorsGrid-TableHeaderSubscribers',
                  }),
                  provider: 'tt',
                  featureFlag: 'istiktokenabled',
                }}
              />

              <div>
                <div>
                  <ProviderIcon height={20} width={20} icon="tw" className="icon" />
                </div>
                {t(TOLGEE_GLOBALS.followers)}
              </div>

              <div>
                <div>
                  <ProviderIcon height={20} width={20} icon="yt" className="icon" />
                </div>
                <T keyName="CreatorsGrid-TableHeaderViews">Views</T>
              </div>
              <div>
                <div>
                  <ProviderIcon height={20} width={20} icon="in" className="icon" />
                </div>
                <T keyName="CreatorsGrid-TableHeaderViews">Views</T>
              </div>

              <HeaderItemFeatureFlag
                itemsHeader={{
                  name: t({
                    defaultValue: 'Views',
                    key: 'CreatorsGrid-TableHeaderViews',
                  }),
                  provider: 'tt',
                  featureFlag: 'istiktokenabled',
                }}
              />
              <div>
                <div>
                  <ProviderIcon height={20} width={20} icon="tw" className="icon" />
                </div>
                {t({
                  defaultValue: 'Views',
                  key: 'CreatorsGrid-TableHeaderViews',
                })}
              </div>
            </>
          )}
        </div>
      ) : null}
      {creators.map((creator, index) => {
        const isSelected = isCreatorInComparison(creator)

        const limit = UserStore?.userInfo?.email?.includes('@winnin') ? 10 : 5
        const showOnSelect = creatorsComparison.length < limit

        if (highlight && !mostLikedVideo) {
          return <Loading key={index} />
        }

        if (highlight && mostLikedVideo) {
          return (
            <CreatorItem
              isPublic={isPublic}
              color={creator.color}
              customListItem={customListItem}
              brandType={creator.brandType || ''}
              highlight={highlight}
              onSelect={handleSelect}
              isSelected={isSelected}
              isBrandGroup={creator.isBrandGroup}
              brandGroup={creator.brandGroup}
              showOnSelect={showOnSelect}
              key={`${creator.creatorId}-${index}`}
              id={creator.creatorId}
              index={index}
              groupId={creator.groupId}
              performance={creator.performance || false}
              title={creator.title}
              country={creator.country}
              thumbnail={creator.thumbnail}
              provider={creator.provider}
              subscribers={creator.subscribers}
              views={creator.views}
              isBrand={creator.isBrand}
              isTrending={creator.isTrending}
              subscribersVariation={creator.subscribersVariation}
              viewsVariation={creator.viewsVariation}
              likes={creator.likes}
              videosVariation={creator.videosVariation}
              avgViewsVariation={creator.avgViewsVariation}
              avgEngagementVariation={creator.avgEngagementVariation}
              engagementVariation={creator.engagementVariation}
              engagementRatioVariation={creator.engagementRatioVariation}
              engagementRatio={creator.engagementRatio}
              videos={creator.videos}
              avgEngagement={creator.avgEngagement}
              avgViews={creator.avgViews}
              mostLikedVideo={mostLikedVideo}
              hideBrandTag={hideBrandTag}
              topics={creator.topics}
              topicMetris={topicMetris}
              className={`search-creator-card card-component-container ${isSelected ? 'selected' : ''}`}
              firstCreatorProvider={firstCreatorProvider}
              showCompareButton={showCompareButton}
              hideTier={hideTier}
              disablePin={disablePin}
            />
          )
        }

        return (
          <CreatorItem
            onSeeVideos={onSeeVideos}
            isPublic={isPublic}
            color={creator.color}
            customListItem={customListItem}
            brandType={creator.brandType || ''}
            highlight={highlight}
            onSelect={handleSelect}
            isSelected={isSelected}
            isBrandGroup={creator.isBrandGroup}
            brandGroup={creator.brandGroup}
            showOnSelect={showOnSelect}
            key={`${creator.creatorId}-${index}`}
            id={creator.creatorId}
            index={index}
            groupId={creator.groupId}
            performance={creator.performance || false}
            title={creator.title}
            country={creator.country}
            thumbnail={creator.thumbnail}
            provider={creator.provider}
            subscribers={creator.subscribers}
            views={creator.views}
            isBrand={creator.isBrand}
            isTrending={creator.isTrending}
            subscribersVariation={creator.subscribersVariation}
            viewsVariation={creator.viewsVariation}
            likes={creator.likes}
            videosVariation={creator.videosVariation}
            avgViewsVariation={creator.avgViewsVariation}
            avgEngagementVariation={creator.avgEngagementVariation}
            engagementVariation={creator.engagementVariation}
            engagementRatioVariation={creator.engagementRatioVariation}
            engagementRatio={creator.engagementRatio}
            videos={creator.videos}
            avgEngagement={creator.avgEngagement}
            avgViews={creator.avgViews}
            hideBrandTag={hideBrandTag}
            topics={creator.topics}
            topicMetris={topicMetris}
            className={`search-creator-card card-component-container ${isSelected ? 'selected' : ''}`}
            firstCreatorProvider={firstCreatorProvider}
            showCompareButton={showCompareButton}
            isFollowing={creator.isFollowing}
            hideTier={hideTier}
            disablePin={disablePin}
            onCreatorClickCallback={onCreatorClickCallback}
            shouldEnableCreatorClick={shouldEnableCreatorClick}
          />
        )
      })}
      {seeMoreAsCard && <SeeMoreCard className="video-grid-see-more-card" onClick={onSeeMoreCard} />}
    </div>
  )
}

CreatorsGrid.propTypes = {
  creators: arrayOf(
    shape({
      creatorId: string.isRequired,
      groupId: string.isRequired,
      title: string.isRequired,
      country: string,
      thumbnail: string.isRequired,
      provider: string.isRequired,
      subscribers: number,
      views: string,
      isBrand: bool.isRequired,
      isTrending: bool,
    })
  ).isRequired,
  customHeader: array,
  customListItem: bool,
  hideBrandTag: bool,
  hideTier: bool,
  disablePin: bool,
  viewMode: oneOf(['list', 'grid']).isRequired,
}

CreatorsGrid.defaultProps = {
  viewMode: 'list',
  showCompareButton: true,
  hideBrandTag: false,
  customListItem: false,
  hideTier: false,
  disablePin: false,
  customHeader: [],
}

CreatorsGrid.displayName = 'CreatorsGrid'

export default observer(CreatorsGrid)
